import { useState, useEffect } from 'react';
import './Dashboard.css';
import { contentService } from '../../services/contentService';
import { config } from '../../config/config';
import adminData from '../../data/admin_access.json';

const themes = [
  { id: 'default', label: 'Default', accent: '#e9ecef' },
  { id: 'blue', label: 'Blue', accent: '#667eea' },
  { id: 'grey', label: 'v2-UI', accent: '#8b5cf6' },
];

const Dashboard = ({ onChatToggle, user, onLogout }) => {
  const sections = contentService.getSections();
  const sectionNames = Object.keys(sections);
  const teaserCards = contentService.getTeaserCards();
  const categories = contentService.getCategories();

  const [activeSection, setActiveSection] = useState(sectionNames[0] || '');
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [welcomeText, setWelcomeText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTheme, setActiveTheme] = useState(config.features.defaultTheme);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const isGreyTheme = activeTheme === 'grey';

  const isAdmin = user?.email && adminData.admins.some(
    a => a.toLowerCase() === user.email.toLowerCase()
  );
  const showThemeSwitcher = !config.features.enableSSO || isAdmin;

  // Apply theme to root
  useEffect(() => {
    const theme = themes.find(t => t.id === activeTheme);
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme.id);
    }
  }, [activeTheme]);

  // Typewriter effect
  useEffect(() => {
    const text = contentService.getDashboardConfig().welcome_text;
    const cfg = contentService.getTypewriterConfig();
    let index = 0;
    let typing = true;
    function typeWriter() {
      if (typing) {
        if (index < text.length) {
          setWelcomeText(text.slice(0, index + 1));
          index++;
          setTimeout(typeWriter, cfg.typing_speed);
        } else {
          setTimeout(() => { typing = false; typeWriter(); }, cfg.pause_duration);
        }
      } else {
        if (index > 0) {
          setWelcomeText(text.slice(0, index - 1));
          index--;
          setTimeout(typeWriter, cfg.erasing_speed);
        } else {
          setWelcomeText('');
        }
      }
    }
    typeWriter();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileOpen && !e.target.closest('.user-info')) setProfileOpen(false);
      if (themeMenuOpen && !e.target.closest('.theme-switcher')) setThemeMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen, themeMenuOpen]);

  const getToolIcon = (title) => contentService.getToolIcon(title);

  const getTeaserForSection = (sectionName) =>
    teaserCards.find(c => c.title === sectionName) || {};

  // Get visible tools based on theme mode
  const getVisibleTools = () => {
    if (isGreyTheme) {
      const cat = categories.find(c => c.id === activeCategory);
      const tools = cat?.tools || [];
      if (!searchTerm) return tools;
      const term = searchTerm.toLowerCase();
      return tools.filter(t =>
        t.title.toLowerCase().includes(term) || t.description.toLowerCase().includes(term)
      );
    }
    const tools = sections[activeSection] || [];
    if (!searchTerm) return tools;
    const term = searchTerm.toLowerCase();
    return tools.filter(t =>
      t.title.toLowerCase().includes(term) || t.description.toLowerCase().includes(term)
    );
  };

  const visibleTools = getVisibleTools();
  const activeTeaser = getTeaserForSection(activeSection);
  const activeCat = categories.find(c => c.id === activeCategory);

  return (
    <div className={`app-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
      {/* Sidebar */}
      <aside className="sidebar" role="navigation" aria-label="Section navigation">
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={contentService.getAppConfig().logo_path} alt="askSRE" />
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'}`}></i>
          </button>
        </div>
        <nav className="sidebar-nav">
          {isGreyTheme ? (
            categories.map(cat => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  className={`sidebar-tab${isActive ? ' active' : ''}`}
                  onClick={() => { setActiveCategory(cat.id); setSearchTerm(''); }}
                  aria-selected={isActive}
                  role="tab"
                  title={cat.label}
                >
                  <i className={`${cat.icon} sidebar-tab-icon`}></i>
                  <span className="sidebar-tab-label">{cat.label}</span>
                </button>
              );
            })
          ) : (
            sectionNames.map(name => {
              const teaser = getTeaserForSection(name);
              const isActive = activeSection === name;
              return (
                <button
                  key={name}
                  className={`sidebar-tab${isActive ? ' active' : ''} ${teaser.colorClass || ''}`}
                  onClick={() => { setActiveSection(name); setSearchTerm(''); }}
                  aria-selected={isActive}
                  role="tab"
                  title={name}
                >
                  <span className="sidebar-tab-label">{name}</span>
                </button>
              );
            })
          )}
        </nav>
      </aside>

      {/* Main Area */}
      <div className="main-area">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            {config.features.enableSearch && (
              <div className="search-container">
                <input
                  type="text"
                  className="search-bar"
                  placeholder={contentService.getSearchConfig().placeholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search tools"
                />
              </div>
            )}
            <div className="user-info">
              {showThemeSwitcher && (
                <div className="theme-switcher">
                  <button
                    className="theme-toggle-btn"
                    onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                    aria-label="Switch theme"
                    title="Switch UI theme"
                  >
                    <i className="fas fa-palette"></i>
                  </button>
                  {themeMenuOpen && (
                    <div className="theme-menu">
                      {themes.map(t => (
                        <button
                          key={t.id}
                          className={`theme-option${activeTheme === t.id ? ' active' : ''}`}
                          onClick={() => { setActiveTheme(t.id); setThemeMenuOpen(false); }}
                        >
                          <span className="theme-dot" style={{ background: t.accent }}></span>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <span className="welcome-text">{welcomeText}</span>
              <div className="profile-pill" onClick={() => setProfileOpen(!profileOpen)}>
                {user?.photoUrl ? (
                  <img src={user.photoUrl} alt="Profile" className="profile-pill-photo" />
                ) : (
                  <span className="profile-pill-avatar">{contentService.getUserInterfaceConfig().user_avatar}</span>
                )}
                <span className="profile-pill-name">Welcome {user?.name || contentService.getDashboardConfig().guest_user}</span>
              </div>
              {profileOpen && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-info">
                    <span className="profile-dropdown-name">{user?.displayName || user?.name || contentService.getDashboardConfig().guest_user}</span>
                    {user?.email && <span className="profile-dropdown-email">{user.email}</span>}
                  </div>
                  {user && onLogout && (
                    <button onClick={onLogout} className="profile-dropdown-logout">
                      <i className="fas fa-sign-out-alt"></i>
                      <span>Sign Out</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Section Header */}
        {isGreyTheme ? (
          <div className="section-header" style={{ borderLeftColor: '#64748b' }}>
            <div className="section-header-inner">
              {activeCat && <i className={`${activeCat.icon} section-cat-icon`}></i>}
              <h2 className="section-name">{activeCat?.label || ''}</h2>
            </div>
          </div>
        ) : (
          <div className="section-header" style={{ borderLeftColor: activeTeaser.color || '#667eea' }}>
            <div className="section-header-inner">
              {activeTeaser.image && (
                <img src={activeTeaser.image} alt={activeSection} className="section-logo" />
              )}
            </div>
          </div>
        )}

        {/* Tiles Grid */}
        <main className={`tiles-area${isGreyTheme && activeCat?.image ? ' tiles-area-full' : ''}`}>
          {isGreyTheme && activeCat?.image ? (
            <div className="about-image-container">
              <img src={activeCat.image} alt={activeCat.label} className="about-team-image" />
            </div>
          ) : visibleTools.length > 0 ? (
            <div className="tiles-grid">
              {visibleTools.map(tool => {
                const toolIcon = getToolIcon(tool.title);
                const allTools = contentService.getTools();
                const toolData = allTools[tool.title] || {};
                const imageUrl = toolData.image || '';
                const features = toolData.features || [];
                const sectionKey = isGreyTheme ? activeCategory : activeSection;

                return (
                  <div key={tool.title} className="tile-wrapper" data-section={sectionKey}>
                    <a href={tool.link} target="_blank" rel="noopener noreferrer">
                      <div className="tile">
                        <div className="tile-image-split">
                          <div className="tile-icon-half">
                            <i className={toolIcon}></i>
                          </div>
                          <div className="tile-picture-half" style={{ backgroundImage: `url('${imageUrl}')` }}></div>
                        </div>
                        <div className="tile-text">
                          <div className="tile-title">{tool.title}</div>
                          <div className="tile-desc">{tool.description}</div>
                        </div>
                      </div>
                    </a>
                    {config.features.enableTileHover && (
                      <div className="tile-hover-tooltip">
                        <h4>Key Features:</h4>
                        <ul>
                          {features.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-results">{searchTerm ? `No tools match "${searchTerm}"` : 'Coming Soon..'}</div>
          )}
        </main>

        {/* Footer */}
        <footer className="footer">
          <p>{contentService.getAppConfig().footer.text.replace(/\d{4}/, new Date().getFullYear())}</p>
        </footer>
      </div>

      {/* Floating Chat */}
      {config.features.enableChat && (
        <div className="floating-chat" onClick={onChatToggle}>
          <span className="chat-icon">{contentService.getFloatingChatConfig().icon}</span>
          <span className="chat-text">{contentService.getFloatingChatConfig().text}</span>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
